import { Box, Typography, Button, Grid, InputAdornment } from "@mui/material";
import { InputBase } from "components/common/Input";
import { useEffect, useState } from "react";
import { BulkOrder, Container } from "types/bulkOrder";

const defaultContainer: Container = {
  weightValue: undefined,
  weightUnit: "lb",
  heightValue: undefined,
  heightUnit: "in",
  widthValue: undefined,
  widthUnit: "in",
  lengthValue: undefined,
  lengthUnit: "in",
};

const MultipleFormInputs = ({
  storageType,
  bulkOrder,
  isSubmitting,
}: {
  storageType: string;
  bulkOrder: BulkOrder | null;
  isSubmitting: boolean;
}) => {
  const [containers, setContainers] = useState<Container[]>(
    bulkOrder?.containers.length ? bulkOrder.containers : [defaultContainer],
  );

  const updateContainer = (index: number, field: "weight" | "height" | "width" | "length", value: number) => {
    setContainers((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              [`${field}Value`]: value,
              [`${field}Unit`]: item[`${field}Unit`] ?? (field === "weight" ? "lb" : "in"),
            }
          : item,
      ),
    );
  };

  const removeContainer = (index: number) => {
    setContainers((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    setContainers(bulkOrder?.containers.length ? bulkOrder.containers : [defaultContainer]);
  }, [bulkOrder?.containers]);

  return (
    <Grid container spacing={4} mt={3}>
      {/* Hidden input to send storage as JSON */}
      <input type="hidden" name="containersAttributes" value={JSON.stringify(containers)} />
      <Grid item xs={12} pl={0}>
        <Button
          variant="outlined"
          color="primary"
          fullWidth
          onClick={() => setContainers([...containers, defaultContainer])}
        >
          + Add {storageType}
        </Button>
      </Grid>

      {containers
        .map((container, index) => (
          <Grid item xs={12} key={index}>
            <Box bgcolor={"#FAFAFA"} py={2} px={3}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" fontWeight={590} pt={"2px"} fontSize={"15px"}>
                    {storageType} {index + 1}
                  </Typography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <Button
                    variant="text"
                    color="warning"
                    sx={{ padding: 0, fontSize: "13px" }}
                    onClick={() => removeContainer(index)}
                    disabled={containers.length <= 1}
                  >
                    Remove
                  </Button>
                </Grid>
                <Grid item xs={6} fontSize={"16px"}>
                  Weight
                </Grid>
                <Grid item xs={6} fontSize={"16px"}>
                  Height
                </Grid>
                <Grid item xs={6} fontSize={"16px"}>
                  <InputBase
                    required
                    type="number"
                    value={container.weightValue ?? ""}
                    onChange={(e) => updateContainer(index, "weight", Number(e.target.value))}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">lb</InputAdornment>,
                    }}
                    inputProps={{
                      min: 0,
                    }}
                  />
                </Grid>
                <Grid item xs={6} fontSize={"16px"}>
                  <InputBase
                    required
                    type="number"
                    value={container.heightValue ?? ""}
                    onChange={(e) => updateContainer(index, "height", Number(e.target.value))}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">in</InputAdornment>,
                    }}
                    inputProps={{
                      min: 0,
                    }}
                  />
                </Grid>
                <Grid item xs={6} fontSize={"16px"}>
                  Width
                </Grid>
                <Grid item xs={6} fontSize={"16px"}>
                  Length
                </Grid>
                <Grid item xs={6} fontSize={"16px"}>
                  <InputBase
                    required
                    type="number"
                    value={container.widthValue ?? ""}
                    onChange={(e) => updateContainer(index, "width", Number(e.target.value))}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">in</InputAdornment>,
                    }}
                    inputProps={{
                      min: 0,
                    }}
                  />
                </Grid>
                <Grid item xs={6} fontSize={"16px"}>
                  <InputBase
                    required
                    type="number"
                    value={container.lengthValue ?? ""}
                    onChange={(e) => updateContainer(index, "length", Number(e.target.value))}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">in</InputAdornment>,
                    }}
                    inputProps={{
                      min: 0,
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>
        ))
        .reverse()}

      <Grid item xs={12} textAlign="center">
        <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
          {isSubmitting ? "Saving..." : "Finish"}
        </Button>
      </Grid>
    </Grid>
  );
};

export default MultipleFormInputs;
